import React from "react";
import { useState } from "react";
import { HEAT_CLASSES, r2 } from "../lib/constants.js";

// Turi sutapti su TakeoffView.jsx naudojama palete, kad zonos spalva sąraše
// atitiktų spalvą ant brėžinio (abi skaičiuojamos pagal poziciją visame sąraše).
const FLOOR_ZONE_PALETTE = ['#f59e0b','#0891b2','#7c3aed','#db2777','#16a34a','#dc2626','#2563eb','#ca8a04','#059669','#9333ea'];

export function SildymasTab({ layer, rooms, markings, setMarkings, ensureCollectorTemplate,
          ensurePipeTemplate, getScale, calibration, selectedDrawingId, templates,
          floorZones, allFloorZones, setFloorZones, floorZonesVisible, setFloorZonesVisible, roomCeilingHeight,
          heatSettings, onHeatSettingsChange, onStartFloorZone, onAddRoomAsZone, onMergeRooms,
          onOpenTemplateEditor }) {

          const ceilingH = parseFloat(roomCeilingHeight) || 0;

          // Ta pati spalva, kuri rodoma ant brėžinio — pagal zonos poziciją VISAME
          // (neišfiltruotame pagal sluoksnį) zonų sąraše, ne šio sluoksnio sąraše.
          function getZoneColor(zone) {
            const list = allFloorZones || floorZones;
            const idx = list.findIndex(z => z.id === zone.id);
            return FLOOR_ZONE_PALETTE[(idx >= 0 ? idx : 0) % FLOOR_ZONE_PALETTE.length];
          }

          // Visi nustatymai iš projekto — nepamirštama
          const heatType    = heatSettings.heatType    || null;
          const heatClass   = heatSettings.heatClass   || 'B';
          const divContours = heatSettings.divContours != null ? Number(heatSettings.divContours) : 12;
          const divPipe     = heatSettings.divPipe     != null ? Number(heatSettings.divPipe)     : 7;

          // Patalpos apjungimas į vieną zoną
          const [mergeMode, setMergeMode] = useState(false);
          const [selectedForMerge, setSelectedForMerge] = useState([]);
          const [mergedZoneName, setMergedZoneName] = useState('');

          // Ar patalpa jau priklauso APJUNGTAI zonų grupei — tik tokios
          // patalpos negalimos rinktis pakartotinai apjungimo sąraše.
          // Pavienės (neapjungtos) patalpos lieka atrakintos.
          function isRoomInMergedZone(room) {
            return floorZones.some(z => z.mergedFrom && z.mergedFrom.includes(room.id));
          }
          // Ar patalpa jau pridėta kaip SAVARANKIŠKA (neapjungta) zona.
          function isRoomIndividuallyAdded(room) {
            return floorZones.some(z => z.label === room.name && Math.abs((z.areaM2||0)-(room.areaM2||0)) < 0.01);
          }

          const scale = getScale();

          // ── Radiatorinis ──
          const radMarkings = markings.filter(m =>
            m.layerId === layer.id && m.drawingId === selectedDrawingId && m.meta?.type === 'radiator'
          );
          const collectorMarkings = markings.filter(m =>
            m.layerId === layer.id && m.drawingId === selectedDrawingId && m.meta?.type === 'collector'
          );
          const pipeMarkings = markings.filter(m =>
            m.layerId === layer.id && m.drawingId === selectedDrawingId && m.meta?.type === 'pipe'
          );

          // Radiatoriui rekomenduojamas galingumas
          function calcRadiatorPower(room) {
            if (!room || !room.areaM2) return null;
            const cls = HEAT_CLASSES.find(c => c.label === heatClass) || HEAT_CLASSES[3];
            return Math.ceil(room.areaM2 * cls.wm2);
          }

          // Automatinis vamzdžių piešimas: kolektorius → radiatorius (Manhattan)
          function drawPipes() {
            if (collectorMarkings.length === 0) { alert('Pirmiausia pažymėkite kolektoriaus vietą.'); return; }
            if (radMarkings.length === 0) { alert('Pirmiausia pažymėkite bent vieną radiatorių.'); return; }
            const tmpl = ensurePipeTemplate();
            const collector = collectorMarkings[0].points[0];
            const newPipes = [];
            let crossings = 0;

            radMarkings.forEach((rad, idx) => {
              const target = rad.points[0];
              const offset = 10 / (scale || 1); // 10mm atstumas tarp dviejų vamzdžių
              // Nukrypimai dviems vamzdžiams
              [0, offset].forEach((off, vi) => {
                // Manhattan kelias: horizontaliai, tada vertikaliai
                const cx = collector.x + off;
                const ty = target.y + off;
                const pipe = {
                  id: Date.now() + idx * 10 + vi + Math.random(),
                  templateId: tmpl.id,
                  drawingId: selectedDrawingId,
                  layerId: layer.id,
                  shape: 'polyline',
                  createdAt: new Date().toISOString().split('T')[0],
                  meta: { type: 'pipe', radId: rad.id, vIndex: vi },
                  points: [
                    { x: cx, y: collector.y },
                    { x: cx, y: ty },
                    { x: target.x + off, y: ty },
                  ],
                };
                newPipes.push(pipe);
                // Tikrinti persidengimus su esamais vamzdžiais
                // (supaprastinta: tiesiog patikriname ar tas pats kelias)
                if (pipeMarkings.some(pm => pm.meta?.radId === rad.id && pm.meta?.vIndex === vi)) crossings++;
              });
            });

            if (crossings > 0) {
              if (!window.confirm(`Įspėjimas: ${crossings} vamzdžių gali persidengti su esamais. Vis tiek nupiešti?`)) return;
            }
            setMarkings(prev => [...prev, ...newPipes]);
          }

          // ── Grindinis ──
          function calcFloorContours(areaM2) {
            return Math.ceil(areaM2 / divContours);
          }
          function calcFloorPipeM(areaM2) {
            return Math.ceil(areaM2 * divPipe);
          }
          const totalContours = floorZones.reduce((a, z) => a + (z.areaM2 ? calcFloorContours(z.areaM2) : 0), 0);
          const totalPipeM = floorZones.reduce((a, z) => a + (z.areaM2 ? calcFloorPipeM(z.areaM2) : 0), 0);
          const totalZoneArea = r2(floorZones.reduce((a, z) => a + (z.areaM2 || 0), 0));
          const totalZoneVolume = ceilingH ? r2(totalZoneArea * ceilingH) : null;

          const inp = {border:'1px solid #e5e7eb',borderRadius:6,padding:'5px 8px',fontSize:12,boxSizing:'border-box'};

          return (
            <div>
              <div style={{fontSize:15,fontWeight:800,color:'#1c1c1c',marginBottom:16,display:'flex',alignItems:'center',gap:8}}>
                <div style={{width:14,height:14,borderRadius:'50%',background:layer.color}}/>
                {layer.label} — Konfigūracija
              </div>

              {/* Šildymo tipo pasirinkimas */}
              <div style={{marginBottom:20}}>
                <div style={{fontSize:11,fontWeight:700,color:'#6b7280',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>Šildymo tipas</div>
                <div style={{display:'flex',gap:10}}>
                  {[['radiatorinis','🔥 Radiatorinis'],['grindinis','🌡 Grindinis']].map(([val,lbl])=>(
                    <div key={val} onClick={()=>onHeatSettingsChange({heatType:val})}
                      style={{flex:1,padding:'14px',borderRadius:10,border:'2px solid '+(heatType===val?layer.color:'#e5e7eb'),
                        background:heatType===val?layer.color+'10':'#fafafa',cursor:'pointer',textAlign:'center'}}>
                      <div style={{fontSize:18,marginBottom:4}}>{lbl.split(' ')[0]}</div>
                      <div style={{fontSize:12,fontWeight:700,color:heatType===val?layer.color:'#374151'}}>{lbl.split(' ')[1]}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── RADIATORINIS ── */}
              {heatType === 'radiatorinis' && (
                <div>
                  {/* Šiluminė klasė */}
                  <div style={{background:'#fff',borderRadius:10,padding:'14px',marginBottom:14,border:'1px solid #e5e7eb'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#6b7280',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>Pastato šiluminė klasė</div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      {HEAT_CLASSES.map(c => (
                        <button key={c.label} onClick={()=>onHeatSettingsChange({heatClass:c.label})}
                          style={{padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',
                            border:'2px solid '+(heatClass===c.label?layer.color:'#e5e7eb'),
                            background:heatClass===c.label?layer.color:'#fff',
                            color:heatClass===c.label?'#fff':'#374151'}}>
                          {c.label} <span style={{fontSize:10,opacity:0.7}}>({c.wm2} W/m²)</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Patalpos ir rekomenduojamas galingumas */}
                  {rooms.length > 0 && (
                    <div style={{background:'#fff',borderRadius:10,padding:'14px',marginBottom:14,border:'1px solid #e5e7eb'}}>
                      <div style={{fontSize:11,fontWeight:700,color:'#6b7280',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>Rekomenduojami radiatorių galingumas</div>
                      <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                        <thead>
                          <tr style={{background:'#f8f9fb'}}>
                            <th style={{padding:'6px 8px',textAlign:'left',fontWeight:600,color:'#6b7280'}}>Patalpa</th>
                            <th style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:'#6b7280'}}>Plotas m²</th>
                            <th style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:'#6b7280'}}>Rekomenduojama W</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rooms.map(room => {
                            const power = calcRadiatorPower(room);
                            return (
                              <tr key={room.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                                <td style={{padding:'6px 8px',fontWeight:600}}>{room.name}</td>
                                <td style={{padding:'6px 8px',textAlign:'right'}}>{room.areaM2 ? room.areaM2.toFixed(1) : '—'}</td>
                                <td style={{padding:'6px 8px',textAlign:'right',fontWeight:700,color:layer.color}}>
                                  {power ? `${power} W` : <span style={{color:'#f59e0b'}}>⚠ kalibruok</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Žymėjimų statusas */}
                  <div style={{background:'#fff',borderRadius:10,padding:'14px',marginBottom:14,border:'1px solid #e5e7eb'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#6b7280',marginBottom:10,textTransform:'uppercase',letterSpacing:0.5}}>Žymėjimai sluoksnyje</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                      {[
                        {label:'Kolektorių',count:collectorMarkings.length,color:'#dc2626'},
                        {label:'Radiatorių',count:radMarkings.length,color:layer.color},
                        {label:'Vamzdžių',count:pipeMarkings.length,color:'#1e88e5'},
                      ].map(item => (
                        <div key={item.label} style={{textAlign:'center',padding:'10px',background:'#f8f9fb',borderRadius:8,border:'1px solid #e5e7eb'}}>
                          <div style={{fontSize:20,fontWeight:900,color:item.color}}>{item.count}</div>
                          <div style={{fontSize:10,color:'#6b7280'}}>{item.label}</div>
                        </div>
                      ))}
                    </div>
                    {!calibration && (
                      <div style={{marginTop:10,padding:'8px 10px',background:'#fef3c7',border:'1px solid #fde68a',borderRadius:6,fontSize:11,color:'#92400e'}}>
                        ⚠ Brėžinys nekalibruotas — vamzdžių pločiai bus apytiksliai.
                      </div>
                    )}
                  </div>

                  {/* Automatinis vamzdžių piešimas */}
                  <div style={{background:'#fff',borderRadius:10,padding:'14px',border:'1px solid #e5e7eb'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#6b7280',marginBottom:10,textTransform:'uppercase',letterSpacing:0.5}}>Automatinis vamzdžių piešimas</div>
                    <div style={{fontSize:12,color:'#374151',marginBottom:12,lineHeight:1.6}}>
                      Norėdami pažymėti <strong>kolektoriaus</strong> ar <strong>radiatoriaus</strong> vietą — naudokite <strong>Šablonai</strong> dalį sluoksnyje <span style={{color:layer.color,fontWeight:700}}>{layer.label}</span>.
                      Programa automatiškai sukurs reikalingus šablonus jei jų nėra.
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button onClick={()=>{ ensureCollectorTemplate(radMarkings.length||1, 1); }}
                        style={{flex:1,background:'#f3f4f6',border:'1px solid #e5e7eb',borderRadius:7,padding:'8px',fontSize:11,fontWeight:700,cursor:'pointer',color:'#374151'}}>
                        🔴 Sukurti kolektoriaus šabloną
                      </button>
                      <button onClick={drawPipes} disabled={collectorMarkings.length===0||radMarkings.length===0}
                        style={{flex:1,background:layer.color,color:'#fff',border:'none',borderRadius:7,padding:'8px',fontSize:11,fontWeight:700,
                          cursor:(collectorMarkings.length>0&&radMarkings.length>0)?'pointer':'not-allowed',
                          opacity:(collectorMarkings.length>0&&radMarkings.length>0)?1:0.5}}>
                        ⚡ Nupiešti vamzdžius ({radMarkings.length * 2} linijų)
                      </button>
                    </div>
                    {pipeMarkings.length > 0 && (
                      <div style={{marginTop:8,padding:'6px 10px',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:6,fontSize:11,color:'#15803d'}}>
                        ✓ Nupiešti {pipeMarkings.length} vamzdžiai
                        {scale && <span> · bendras ilgis ≈ {(pipeMarkings.reduce((a,m)=>{
                          if(!m.points||m.points.length<2) return a;
                          let l=0; for(let i=1;i<m.points.length;i++) l+=Math.sqrt(Math.pow(m.points[i].x-m.points[i-1].x,2)+Math.pow(m.points[i].y-m.points[i-1].y,2));
                          return a+l*scale;
                        },0)).toFixed(1)} m</span>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── GRINDINIS ── */}
              {heatType === 'grindinis' && (
                <div>
                  {/* Skaičiavimo formulės nustatymai */}
                  <div style={{background:'#fff',borderRadius:10,padding:'12px 14px',marginBottom:14,border:'1px solid #e5e7eb'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:0.5,marginBottom:8}}>Skaičiavimo formulė</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                      <div>
                        <div style={{fontSize:11,color:'#374151',marginBottom:4}}>
                          Kontūrų daliklis <span style={{color:'#9ca3af'}}>(plotas ÷ X)</span>
                        </div>
                        <input type="number" min="1" step="0.1" value={divContours}
                          onChange={e=>onHeatSettingsChange({divContours:parseFloat(e.target.value)||12})}
                          style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:6,padding:'5px 8px',fontSize:13,fontWeight:700,boxSizing:'border-box'}}/>
                        <div style={{fontSize:10,color:'#9ca3af',marginTop:2}}>Dabartinė: {divContours}</div>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:'#374151',marginBottom:4}}>
                          Vamzdžio koeficientas <span style={{color:'#9ca3af'}}>(plotas × X)</span>
                        </div>
                        <input type="number" min="1" step="0.1" value={divPipe}
                          onChange={e=>onHeatSettingsChange({divPipe:parseFloat(e.target.value)||7})}
                          style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:6,padding:'5px 8px',fontSize:13,fontWeight:700,boxSizing:'border-box'}}/>
                        <div style={{fontSize:10,color:'#9ca3af',marginTop:2}}>Dabartinis: {divPipe}</div>
                      </div>
                    </div>
                  </div>

                  {/* Patalpos apjungimas į zoną */}
                  {rooms.filter(r=>r.areaM2).length >= 2 && (
                    <div style={{background:'#fff',borderRadius:10,padding:'12px 14px',marginBottom:14,border:'1px solid #e5e7eb'}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                        <div style={{fontSize:11,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:0.5}}>Apjungti patalpas į zoną</div>
                        <button onClick={()=>{setMergeMode(v=>!v);setSelectedForMerge([]);setMergedZoneName('');}}
                          style={{background:mergeMode?'#f3f4f6':'#383853',color:mergeMode?'#374151':'#fff',
                            border:'none',borderRadius:6,padding:'4px 10px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          {mergeMode ? '✕ Atšaukti' : '+ Apjungti'}
                        </button>
                      </div>
                      {mergeMode && (
                        <div>
                          <div style={{fontSize:11,color:'#374151',marginBottom:6}}>Pasirinkite patalpas apjungimui:</div>
                          <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:8}}>
                            {rooms.filter(r=>r.areaM2).map(r=>{
                              const sel = selectedForMerge.includes(r.id);
                              const locked = isRoomInMergedZone(r);
                              return (
                                <button key={r.id} disabled={locked}
                                  onClick={()=>{ if (locked) return; setSelectedForMerge(prev=>sel?prev.filter(x=>x!==r.id):[...prev,r.id]); }}
                                  title={locked?'Patalpa jau priklauso apjungtai zonų grupei':undefined}
                                  style={{padding:'4px 10px',borderRadius:6,fontSize:11,fontWeight:600,
                                    cursor:locked?'not-allowed':'pointer',opacity:locked?0.45:1,
                                    border:'2px solid '+(sel?layer.color:'#e5e7eb'),
                                    background:sel?layer.color+'15':'#f9fafb',
                                    color:sel?layer.color:'#374151'}}>
                                  {locked?'🔒 ':(sel?'✓ ':'')}{r.name} ({r2(r.areaM2).toFixed(2)} m²)
                                </button>
                              );
                            })}
                          </div>
                          {selectedForMerge.length >= 2 && (
                            <div>
                              <div style={{fontSize:11,color:'#374151',marginBottom:4}}>
                                Bendras plotas: <strong style={{color:layer.color}}>
                                  {r2(rooms.filter(r=>selectedForMerge.includes(r.id)).reduce((a,r)=>a+(r.areaM2||0),0)).toFixed(2)} m²
                                </strong>
                              </div>
                              <div style={{display:'flex',gap:6,marginTop:6}}>
                                <input value={mergedZoneName} onChange={e=>setMergedZoneName(e.target.value)}
                                  placeholder={rooms.filter(r=>selectedForMerge.includes(r.id)).map(r=>r.name).join(' + ')}
                                  style={{flex:1,border:'1px solid #e5e7eb',borderRadius:6,padding:'5px 8px',fontSize:12,boxSizing:'border-box'}}/>
                                <button onClick={()=>{
                                  onMergeRooms(selectedForMerge, mergedZoneName);
                                  setMergeMode(false);
                                  setSelectedForMerge([]);
                                  setMergedZoneName('');
                                }}
                                  style={{background:layer.color,color:'#fff',border:'none',borderRadius:6,padding:'5px 14px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                  ✓ Apjungti
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Zonų suvestinė */}
                  <div style={{background:'#fff',borderRadius:10,padding:'14px',marginBottom:14,border:'1px solid #e5e7eb'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{fontSize:11,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:0.5}}>Grindinio šildymo zonos</div>
                        <button onClick={()=>setFloorZonesVisible(v=>!v)}
                          title={floorZonesVisible?'Paslėpti zonas':'Rodyti zonas'}
                          style={{background:'none',border:'1px solid #e5e7eb',borderRadius:4,padding:'1px 5px',cursor:'pointer',fontSize:11,color:floorZonesVisible?'#059669':'#9ca3af',fontWeight:700}}>
                          {floorZonesVisible?'👁':'🙈'}
                        </button>
                      </div>
                      <button onClick={onStartFloorZone}
                        style={{background:layer.color,color:'#fff',border:'none',borderRadius:6,padding:'5px 12px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                        ✏ Piešti zoną
                      </button>
                    </div>

                    {/* Patalpos kaip zonos */}
                    {rooms.filter(r=>r.points&&r.areaM2).length > 0 && (
                      <div style={{marginBottom:10,padding:'8px 10px',background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:6}}>
                        <div style={{fontSize:10,fontWeight:700,color:'#0369a1',marginBottom:5}}>
                          💡 Pridėti iš patalpos sąrašo (automatiškai perpiešia ant sluoksnio):
                        </div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                          {rooms.filter(r=>r.points&&r.areaM2).map(r => {
                            const individuallyAdded = isRoomIndividuallyAdded(r);
                            const inMergedGroup = isRoomInMergedZone(r);
                            return (
                              <button key={r.id} onClick={()=>{
                                  if (inMergedGroup) return;
                                  if (individuallyAdded) {
                                    setFloorZones(prev => prev.filter(z => !(z.label===r.name && Math.abs((z.areaM2||0)-(r.areaM2||0))<0.01)));
                                  } else {
                                    onAddRoomAsZone(r);
                                  }
                                }}
                                disabled={inMergedGroup}
                                title={inMergedGroup ? 'Patalpa jau priklauso apjungtai zonai' : (individuallyAdded ? 'Spauskite, kad pašalintumėte iš zonų' : undefined)}
                                style={{background:individuallyAdded?'#f0fdf4':(inMergedGroup?'#f3f4f6':'#fff'),
                                  border:'1px solid '+(individuallyAdded?'#86efac':(inMergedGroup?'#d1d5db':'#bae6fd')),
                                  borderRadius:4,padding:'3px 8px',fontSize:10,cursor:inMergedGroup?'not-allowed':'pointer',fontWeight:600,
                                  opacity:(inMergedGroup&&!individuallyAdded)?0.55:1,
                                  color:individuallyAdded?'#15803d':(inMergedGroup?'#6b7280':'#0369a1')}}>
                                {individuallyAdded?'✓':(inMergedGroup?'🔒':'+')} {r.name} ({r2(r.areaM2)}m²)
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {floorZones.length === 0 && (
                      <div style={{textAlign:'center',color:'#9ca3af',padding:'16px 0',fontSize:12}}>
                        Nėra zonų. Spauskite „Piešti zoną" arba pridėkite iš patalpos.
                      </div>
                    )}

                    {floorZones.length > 0 && (
                      <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                        <thead>
                          <tr style={{background:'#f8f9fb'}}>
                            <th style={{padding:'6px 8px',textAlign:'left',fontWeight:600,color:'#6b7280'}}>Zona</th>
                            <th style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:'#6b7280'}}>Plotas m²</th>
                            {ceilingH > 0 && <th style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:'#6b7280'}}>Tūris m³</th>}
                            <th style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:'#6b7280'}}>Kontūrai</th>
                            <th style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:'#6b7280'}}>Vamzdis m</th>
                            <th style={{padding:'6px 4px'}}/>
                          </tr>
                        </thead>
                        <tbody>
                          {floorZones.map((z) => {
                            const zoneVol = (z.areaM2 && ceilingH) ? r2(z.areaM2 * ceilingH) : null;
                            return (
                            <tr key={z.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                              <td style={{padding:'6px 8px',fontWeight:600}}>
                                <div style={{display:'flex',alignItems:'center',gap:5}}>
                                  <div style={{width:8,height:8,borderRadius:2,background:getZoneColor(z),flexShrink:0}}/>
                                  {z.label}
                                  {z.mergedFrom && <span style={{fontSize:9,color:'#9ca3af',fontWeight:400}}>&nbsp;(apjungta)</span>}
                                </div>
                              </td>
                              <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600}}>
                                {z.areaM2 ? r2(z.areaM2).toFixed(2) : <span style={{color:'#f59e0b'}}>⚠ kalibruok</span>}
                              </td>
                              {ceilingH > 0 && (
                                <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600}}>
                                  {zoneVol != null ? zoneVol.toFixed(2) : '—'}
                                </td>
                              )}
                              <td style={{padding:'6px 8px',textAlign:'right',fontWeight:700,color:layer.color}}>
                                {z.areaM2 ? calcFloorContours(z.areaM2) : '—'}
                              </td>
                              <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600}}>
                                {z.areaM2 ? calcFloorPipeM(z.areaM2) : '—'}
                              </td>
                              <td style={{padding:'6px 4px'}}>
                                <button onClick={()=>setFloorZones(prev=>prev.filter(x=>x.id!==z.id))}
                                  style={{background:'none',border:'none',cursor:'pointer',fontSize:14,color:'#d1d5db'}}>×</button>
                              </td>
                            </tr>
                          );})}
                        </tbody>
                        <tfoot>
                          <tr style={{background:'#f8f9fb',borderTop:'2px solid #e5e7eb'}}>
                            <td style={{padding:'8px',fontWeight:700,fontSize:12}}>IŠ VISO</td>
                            <td style={{padding:'8px',textAlign:'right',fontWeight:800,fontSize:13}}>{totalZoneArea.toFixed(2)}</td>
                            {ceilingH > 0 && <td style={{padding:'8px',textAlign:'right',fontWeight:800,fontSize:13}}>{totalZoneVolume != null ? totalZoneVolume.toFixed(2) : '—'}</td>}
                            <td style={{padding:'8px',textAlign:'right',fontWeight:800,color:layer.color,fontSize:14}}>{totalContours}</td>
                            <td style={{padding:'8px',textAlign:'right',fontWeight:800,fontSize:14}}>{totalPipeM} m</td>
                            <td/>
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </div>

                  {/* Kolektoriaus rekomendacija */}
                  {totalContours > 0 && (
                    <div style={{background:'#fff',borderRadius:10,padding:'14px',marginBottom:14,border:'1px solid #e5e7eb'}}>
                      <div style={{fontSize:11,fontWeight:700,color:'#6b7280',marginBottom:10,textTransform:'uppercase',letterSpacing:0.5}}>Kolektoriaus rekomendacija</div>
                      <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:8}}>
                        <div style={{fontSize:28}}>🔴</div>
                        <div>
                          <div style={{fontSize:13,fontWeight:800,color:'#dc2626'}}>KOL DEB {totalContours}ž.</div>
                          <div style={{fontSize:11,color:'#6b7280'}}>Kolektorius su {totalContours} žiedais</div>
                        </div>
                        <button onClick={()=>{
                          ensureCollectorTemplate(totalContours, totalContours);
                          if (onOpenTemplateEditor) {
                            const colName = `KOL DEB ${totalContours}ž.`;
                            const existing = templates.find(t => t.name === colName);
                            const updatedTmpl = existing
                              ? { ...existing,
                                  positions: existing.positions && existing.positions.length > 0
                                    ? existing.positions.map((p,i) => i===0 ? {...p, unit:'vnt.', qtyPerUnit: totalContours} : p)
                                    : [{ id: Date.now(), name: colName, groupName: 'Šildymo kolektoriai', groupId: null, unit: 'vnt.', qtyPerUnit: totalContours, price: 0 }]
                                }
                              : { id: Date.now(), name: colName, groupName: 'Šildymo kolektoriai',
                                  shape: 'rect', color: '#dc2626', strokeWidth: 2, size: 20,
                                  positions: [{ id: Date.now()+1, name: colName, groupName: 'Šildymo kolektoriai', groupId: null, unit: 'vnt.', qtyPerUnit: totalContours, price: 0 }]
                                };
                            onOpenTemplateEditor(updatedTmpl);
                          }
                        }}
                          style={{marginLeft:'auto',background:'#dc2626',color:'#fff',border:'none',borderRadius:7,padding:'7px 14px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          ✓ Sukurti / Redaguoti šabloną
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Vamzdžio suvestinė */}
                  {totalPipeM > 0 && (
                    <div style={{background:'#fff',borderRadius:10,padding:'14px',border:'1px solid #e5e7eb'}}>
                      <div style={{fontSize:11,fontWeight:700,color:'#6b7280',marginBottom:10,textTransform:'uppercase',letterSpacing:0.5}}>Vamzdžio kiekis</div>
                      {/* Pavadinimo redagavimas */}
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                        <span style={{fontSize:11,color:'#374151',flexShrink:0}}>Šablono pavadinimas:</span>
                        <input
                          value={heatSettings.pipeName != null ? heatSettings.pipeName : 'VM PE-RT D18'}
                          onChange={e=>onHeatSettingsChange({pipeName: e.target.value})}
                          placeholder="VM PE-RT D18"
                          style={{flex:1,border:'1px solid #e5e7eb',borderRadius:6,padding:'4px 8px',fontSize:12,fontWeight:600}}/>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:8,marginBottom:10}}>
                        <div style={{fontSize:24}}>🔵</div>
                        <div>
                          <div style={{fontSize:14,fontWeight:800,color:'#1e88e5'}}>{totalPipeM} m</div>
                          <div style={{fontSize:11,color:'#6b7280'}}>
                            {heatSettings.pipeName || 'VM PE-RT D18'} · {floorZones.length} zonom
                          </div>
                        </div>
                        <button onClick={()=>{
                          const pipeName = heatSettings.pipeName || 'VM PE-RT D18';
                          ensurePipeTemplate(pipeName, totalPipeM);
                          if (onOpenTemplateEditor) {
                            // Sukuriame atnaujintą objektą tiesiai čia
                            const existing = templates.find(t => t.name === pipeName);
                            const updatedTmpl = existing
                              ? { ...existing, shape: 'circle', strokeWidth: 1,
                                  positions: existing.positions && existing.positions.length > 0
                                    ? existing.positions.map((p,i) => i===0 ? {...p, unit:'m', qtyPerUnit: totalPipeM} : p)
                                    : [{ id: Date.now(), name: pipeName, groupName: 'Vandentiekio vamzdynas', groupId: null, unit: 'm', qtyPerUnit: totalPipeM, price: 0 }]
                                }
                              : { id: Date.now(), name: pipeName, groupName: 'Vandentiekio vamzdynas',
                                  shape: 'circle', color: '#1e88e5', strokeWidth: 1, size: 6,
                                  positions: [{ id: Date.now()+1, name: pipeName, groupName: 'Vandentiekio vamzdynas', groupId: null, unit: 'm', qtyPerUnit: totalPipeM, price: 0 }]
                                };
                            onOpenTemplateEditor(updatedTmpl);
                          }
                        }}
                          style={{marginLeft:'auto',background:'#1e88e5',color:'#fff',border:'none',borderRadius:7,padding:'7px 14px',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          ✓ Sukurti / Redaguoti šabloną
                        </button>
                      </div>
                      <div style={{fontSize:11,color:'#9ca3af'}}>
                        * Vamzdžio kiekis skaičiuojamas: plotas × {divPipe}. Šablonas suvestinėje rodys faktinį žymėtą ilgį.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!heatType && (
                <div style={{textAlign:'center',color:'#9ca3af',padding:'30px 0',fontSize:13}}>
                  Pasirinkite šildymo tipą aukščiau, kad tęstumėte konfigūraciją.
                </div>
              )}
            </div>
          );
        }
